import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import { useCallback, useMemo, useRef, useState } from 'react'

import { PropertiesChangeCallback } from '@/components/Properties/types'
import { StructureDto } from '@/endpoints/models'
import {
	useAppDispatch,
	useAppStore,
	useDebounceCallback,
	useTableColumns,
} from '@/hooks'
import { findSystemNodeId } from '@/store/modules/node/helpers'
import { arrayMoveIndex, assignCollectionOrder, NativeMap } from '@/utils'

import { PanelProperties, TableProperties } from '../Properties'
import { LastEditedCellContext } from './LastEditedCellContext'
import { MainTable } from './MainTable'
import { LastEditedCellProps, TableAndPropertiesProps } from './types'
import { findOriginalIndex, updateTableAndPropertiesValues } from './utils'

export const TableAndProperties = <Item extends Record<string, any>>({
	properties,
	data,
	node,
	onChange,
	itemsKey,
	createEmpty,
	tabKey,
	isReadonly,
	idKey = 'id',
	existsKey = 'name',
	idCounterKey,
	errors,
	panelPropertiesMinWidth,
	panelPropertiesDefaultOpen,
	disablePanelProperties,
	technicalColumns,
	maxWidth,
	children,
}: TableAndPropertiesProps<Item>) => {
	const dispatch = useAppDispatch()
	const openedTabs = useAppStore((store) => store.tab.openedTabs)
	const nodeId = useAppStore((store) => store.tab.selectedId)
	const systemsSlice = useAppStore((state) => state.system.systems)
	const nodes = useAppStore((state) => state.node.nodes)
	const systemId = findSystemNodeId(node, nodes as NativeMap<StructureDto>)
	const isCodeLowerCase = systemsSlice[systemId]?.form?.letterCaseToggle
	const tabInfo = openedTabs.find((tab) => tab.nodeId === nodeId)
	const isEditMode = Boolean(tabInfo?.editMode)
	const [refWrapper, setRefWrapper] = useState<HTMLDivElement | undefined>()

	const isDeletingRef = useRef(false)

	const refCallback = useCallback((htmlNode: HTMLDivElement) => {
		if (htmlNode) {
			setRefWrapper(htmlNode)
		}
	}, [])

	const [lastEditedCell, setLastEditedCell] = useState<LastEditedCellProps>({
		rowId: -1,
		fieldName: '',
	})

	const lastEditedCellRef = useRef<HTMLTableCellElement>(null)

	const items = useMemo(() => {
		const dataItems = data[itemsKey]
		const isLastItemExists = get(last(dataItems), existsKey)
		const shouldAddNewItem = isEmpty(dataItems) || isLastItemExists
		const newItem = createEmpty(dataItems)

		return isEditMode && shouldAddNewItem ? [...dataItems, newItem] : dataItems
	}, [data, itemsKey, existsKey, createEmpty, isEditMode])

	const handleChangeImpl: PropertiesChangeCallback = useCallback(
		async ({
			fieldId,
			fieldName,
			fieldValue,
			uuid,
			rowIndex,
			fieldOptions,
		}) => {
			const originalIndex = findOriginalIndex(
				fieldId,
				uuid,
				items,
				itemsKey,
				rowIndex,
			)

			if (isDeletingRef.current) {
				isDeletingRef.current = false
				return
			}
			await updateTableAndPropertiesValues({
				createDynamicEmptyRow: createEmpty,
				fieldName,
				data,
				dispatch,
				idCounterKey,
				idKey,
				items,
				itemsKey,
				onChange,
				originalIndex,
				rowIndex,
				setLastEditedCell,
				fieldValue,
				fieldId,
				isCodeLowerCase,
				fieldOptions,
				properties,
			})
		},
		[onChange, items, properties],
	)

	const handleChangeRef = useRef(handleChangeImpl)
	handleChangeRef.current = handleChangeImpl
	const handleChange = useCallback<PropertiesChangeCallback>((args) => {
		return handleChangeRef.current(args)
	}, [])

	const columns = useTableColumns({
		tableData: [...items, ...(technicalColumns ?? [])],
		properties,
		refWrapper,
		lastEditedCell,
		lastEditedCellRef,
		node,
	})

	const onSaveData = useDebounceCallback((newItems: Item[]) => {
		const updatedData = {
			[itemsKey]: assignCollectionOrder(newItems),
		}

		onChange(updatedData)
	})

	const handleDelete = useCallback(
		(deletedItem: Item) => {
			isDeletingRef.current = true
			const remaining = items.filter(
				(item: Item) => item[idKey] !== deletedItem[idKey],
			)
			const updatedData = {
				[itemsKey]: assignCollectionOrder(remaining),
			}

			onChange(updatedData)
		},
		[idKey, items, itemsKey, onChange],
	)

	// only for old table handlers
	const tab = useAppStore((store) =>
		store.tab.openedTabs.find((t) => t?.nodeId === node?.id),
	)

	const itemsForm = useMemo(() => data[itemsKey] || [], [data, itemsKey])
	const resolveIsDeletable = (item: Item, itemIndex: number) => {
		if (itemIndex + 1 < items.length) {
			return true
		}

		return !!item[existsKey]
	}

	const selectedItemIndex = useMemo(
		() => ((tab || { properitesItems: {} }).propertiesItems || {})[tabKey],
		[tab, tabKey],
	)

	const handleRowOrderChanged = (oldIndex: number, newIndex: number) => {
		const reordered = assignCollectionOrder(
			arrayMoveIndex(itemsForm, oldIndex, newIndex),
		)

		onChange({
			[itemsKey]: reordered,
		})
	}

	const NEW_TABLE = true

	const renderTable = useCallback(
		(propertiesWidth: number, propertiesHidden: boolean) => {
			return (
				<div ref={refCallback} className="h-full min-h-0 flex-1 overflow-auto">
					{NEW_TABLE ? (
						<MainTable
							columns={columns}
							handleChange={handleChange}
							handleDelete={handleDelete}
							items={items}
							lastEditedCell={lastEditedCell}
							onSaveData={onSaveData}
							isEditMode={isEditMode}
							isReadonly={isReadonly}
							properties={properties}
							technicalColumns={technicalColumns}
							maxWidth={maxWidth}
						/>
					) : (
						<TableProperties
							items={items}
							selectedItemIndex={selectedItemIndex}
							readonly={false}
							properties={properties}
							onChange={handleChange}
							onDelete={handleDelete}
							isDeletable={resolveIsDeletable}
							isRowOrderable={false}
							isReadonly={isReadonly}
							onRowOrderChanged={handleRowOrderChanged}
							errors={errors}
							propertiesHidden={propertiesHidden}
						>
							{children}
						</TableProperties>
					)}
				</div>
			)
		},
		[properties, refCallback, items],
	)

	const lastEditedCellContextValue = useMemo(
		() => ({ lastEditedCell, lastEditedCellRef }),
		[lastEditedCell],
	)

	if (disablePanelProperties) {
		return (
			<LastEditedCellContext.Provider value={lastEditedCellContextValue}>
				<div className="flex h-full min-h-0 flex-1 flex-col pt-5">
					{renderTable(0, true)}
				</div>
			</LastEditedCellContext.Provider>
		)
	}

	return (
		<LastEditedCellContext.Provider value={lastEditedCellContextValue}>
			<div className="relative flex h-full min-h-0 flex-1 flex-col overflow-hidden pt-5 pb-4">
				{renderTable(0, true)}
				<PanelProperties
					node={node}
					items={items}
					properties={properties}
					listOfValues={items}
					onChange={handleChange}
					errors={errors}
					tabKey={tabKey}
					panelPropertiesMinWidth={panelPropertiesMinWidth}
					panelPropertiesDefaultOpen={panelPropertiesDefaultOpen}
				/>
			</div>
		</LastEditedCellContext.Provider>
	)
}
